import { refineParticleTreeNode, ResolveFurniture, getUserOptions } from './furnitureResolution';
import { getConfiguration } from '../compatibility';
import { PropertySelections } from '../types';
import { ParticleTreeNodeWithAttachments, TagAttachmentOptions } from './particleNodes';
import { AnyAction, Dispatch } from 'redux';
import * as CoreStore from '../../../store/core';
import { AppError } from '../../../data/appError';
import * as ConfigurationActions from '../../../store/actions/configuration';
import { logError, logWarning } from '../../../store/actions/logging';

export type FurnitureChoiceResult = {
    furnitureChoiceTree: ParticleTreeNodeWithAttachments;
    attachmentOptions: TagAttachmentOptions[];
};

export type ResolveFurnitureChoiceTree = (
    selections: PropertySelections,
) => Promise<FurnitureChoiceResult | null>;

export async function resolveFurnitureChoiceTree(
    gavelUrl: string,
    tenantId: string,
    spaceId: string,
    selections: PropertySelections,
    modelsUrl: string,
    dispatch: Dispatch<AnyAction>,
    disableErrorHandling: boolean,
): Promise<FurnitureChoiceResult | null> {
    const configuration = await getConfiguration(gavelUrl, tenantId, spaceId, selections);

    if (!configuration) {
        return null;
    }

    try {
        const furnitureChoiceTree = await ResolveFurniture(
            configuration.parts,
            modelsUrl,
            dispatch,
            disableErrorHandling,
        );
        const attachmentOptions = getUserOptions(furnitureChoiceTree);
        return { furnitureChoiceTree, attachmentOptions };
    } catch (err: unknown) {
        if (err instanceof Error) {
            dispatch(
                logError(
                    `failed to resolve furniture: '${err.message}' from particles `,
                    configuration.parts,
                ),
            );
            dispatch(
                logWarning(
                    'THIS IS UNRECOVERABLE, NO CHANGES TO BASKET OR VIEWER WILL OCCUR FROM THIS SELECTION',
                ),
            );
            if (!disableErrorHandling) {
                dispatch(CoreStore.error(AppError.CONFIGURATION_DATA_LOAD_FAILED));
                dispatch(
                    ConfigurationActions.errorAction({
                        reason: `failed to resolve furniture: ${err.message} from particles ${configuration.parts}`,
                        type: 'catch all',
                    }),
                );
            }
        }
        return null;
    }
}
export function applyAttachmentSelections(
    attachmentOptions: TagAttachmentOptions[],
    attachmentProposals: Record<string, string>,
    furnitureChoiceTree: ParticleTreeNodeWithAttachments,
) {
    const attachmentSelections = resolveAttachmentSelections(
        attachmentOptions,
        attachmentProposals,
    );

    const furnitureTree = refineParticleTreeNode(furnitureChoiceTree, attachmentSelections);
    return { furnitureTree, attachmentSelections };
}
function resolveAttachmentSelections(
    attachmentOptions: TagAttachmentOptions[] | undefined,
    attachmentProposals: Record<string, string>,
) {
    return {
        ...(attachmentOptions
            ? attachmentOptions.reduce(
                  (s, opt) => ({
                      ...s,
                      [opt.tag]: opt.selected,
                  }),
                  {} as Record<string, string>,
              )
            : {}),
        ...Object.entries(attachmentProposals)
            .filter(([attachmentOption, value]) =>
                attachmentOptions?.find(a => a.tag === attachmentOption)?.options.includes(value),
            )
            .reduce(
                (s, [attachmentOption, value]) => ({
                    ...s,
                    [attachmentOption]: value,
                }),
                {} as Record<string, string>,
            ),
    };
}
